import { IWeatherForecastDaily } from '../../../../domain/weather/openWeather';
import Divider from 'antd/lib/divider';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import App from 'antd/lib/app';
import Skeleton from 'antd/lib/skeleton';
import { useMemo } from 'react';
import { theme } from 'antd/lib';
import { useForecastWeatherQuery } from '../../../../domain/weather/queries';
import { Market } from 'src/domain/market-prices/interface';

interface ILocation {
    city: string;
    state: string;
    market?: Market;
    lat: number;
    lng: number;
}

interface IWeatherForecastRowProps {
    location?: ILocation;
    activeEventMarketsMap: Map<string, string[]>;
}

export function WeatherForecastRow({ location, activeEventMarketsMap }: IWeatherForecastRowProps) {
    const { token: themeColors } = theme.useToken();
    const { notification } = App.useApp();

    const { data, isLoading, isError, error } = useForecastWeatherQuery(location!, {
        enabled: !!location?.lat && !!location?.lng,
        keepPreviousData: true,
    });

    if (isError) {
        notification.error({ key: 'weather-forecast', message: error.message || 'Cannot get weather forecast' });
    }

    const weatherForecast = useMemo(() => {
        if (!data) return [];

        return prepareDailyForecast(data, location, activeEventMarketsMap);
    }, [data, location, activeEventMarketsMap]);

    return (
        <>
            <Divider orientation="left" style={{ margin: '8px 0 0 0', borderColor: themeColors.colorTextSecondary }}>
                {location?.market ? `${location?.market?.toUpperCase()} (${location?.city}, ${location?.state})` : 'Center of Map'}
            </Divider>
            {isLoading ? (
                <Skeleton loading paragraph={{ rows: 3 }} active />
            ) : (
                <Row gutter={8}>
                    {weatherForecast?.map(day => (
                        <Col
                            style={{
                                flexBasis: '20%',
                                width: '20%',
                            }}
                            key={`${day.dayName}-${location?.lat}-${location?.lng}`}
                        >
                            <div className="weather-forecast-day-card">
                                <div className="content">
                                    <div className="conditions">
                                        <span className="icon-wrap icon">
                                            <i className={`wi ${day.icon}`}></i>
                                        </span>
                                        <h1 style={{ margin: '10px 0' }}>{day.temperature}°</h1>
                                    </div>
                                    <div className={`dr-chance dr-chance-${day.drChance}`}>&nbsp;</div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            )}
        </>
    );
}

function prepareDailyForecast(
    dailyForecast: IWeatherForecastDaily[],
    location: IWeatherForecastRowProps['location'],
    activeEventMarketsMap: IWeatherForecastRowProps['activeEventMarketsMap']
) {
    const MAX_FORECAST_DAYS = 5;

    const daysToDisplay = Math.min(dailyForecast.length, MAX_FORECAST_DAYS);
    const fiveDayForecast = dailyForecast.slice(0, daysToDisplay);

    if (fiveDayForecast.length) {
        const todayMarkets = activeEventMarketsMap.get('today');
        const tomorrowMarkets = activeEventMarketsMap.get('tomorrow');

        const existTodayEvent =
            (!location?.market && todayMarkets?.length) || (location?.market && todayMarkets?.includes(location?.market?.toLowerCase()));
        const existTomorrowEvent =
            (!location?.market && tomorrowMarkets?.length) || (location?.market && tomorrowMarkets?.includes(location?.market?.toLowerCase()));

        existTodayEvent && (fiveDayForecast[0].drChance = 3);
        existTomorrowEvent && (fiveDayForecast[1].drChance = 3);
    }

    return fiveDayForecast;
}
